<template>
  <div class="ai-types">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t('__aiTypeManagement') }}</h2>

      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap">
        <div class="me-auto"></div>
        <button
          @click="showModal()"
          class="mb-2 fs-5 btn btn-primary px-2 rounded-circle lh-sm"
        >
          <i class="bi bi-plus"></i>
        </button>
      </div>

      <div class="card">
        <div v-if="loading" class="card-body text-center">
          <i class="fas fa-spinner fa-spin"></i> {{ $t('__loading') }}
        </div>
        <div v-else-if="types && types.length > 0" class="card-body">
          <b-table
            :responsive="$store.state.windowWidth > $store.state.breakpoints.sm"
            :stacked="$store.state.windowWidth <= $store.state.breakpoints.sm"
            striped
            borderless
            no-provider-filtering
            head-variant="light"
            :items="types"
            :fields="fields"
          >
            <template v-slot:head()="scope">
              <div class="text-nowrap">
                {{ scope.label }}
              </div>
            </template>

            <template v-slot:cell(sort_order)="data">
              <input
                type="number"
                v-model.number="data.item.sort_order"
                @change="updateType(data.item)"
                class="form-control form-control-sm"
                style="width: 60px"
              >
            </template>

            <template v-slot:cell(is_active)="data">
              <div class="status-switch">
                <input
                  type="checkbox"
                  :id="'switch-' + data.index"
                  v-model="data.item.is_active"
                  @click="toggleStatus(data.item)"
                />
                <label :for="'switch-' + data.index"></label>
              </div>
            </template>

            <template v-slot:cell(actions)="data">
              <div class="d-flex">
                <button
                  @click="showModal(data.item.id)"
                  type="button"
                  class="btn btn-link px-1 text-dark"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__edit')"
                >
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  @click="confirmDelete(data.item)"
                  type="button"
                  class="btn btn-link px-1 text-dark"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__delete')"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </template>
          </b-table>
        </div>
        <div v-else class="card-body text-center text-muted">
          {{ $t('__noAiTypeData') }}
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div class="modal fade cms-style" tabindex="-1" id="typeModal" ref="typeModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="saveType">
            <div class="modal-header">
              <h5 class="modal-title fw-medium">
                {{ form.id ? $t('__editAiType') : $t('__addAiType') }}
              </h5>
              <button
                type="button"
                class="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label for="code" class="form-label fw-medium required">{{ $t('__aiTypeCode') }}</label>
                <input
                  type="text"
                  v-model="form.code"
                  class="form-control"
                  id="code"
                  maxlength="20"
                  :disabled="!!form.id"
                  required
                >
                <small class="text-muted">{{ $t('__aiTypeCodeHint') }}</small>
              </div>

              <div class="mb-3">
                <label for="name" class="form-label fw-medium required">{{ $t('__aiTypeName') }}</label>
                <input
                  type="text"
                  v-model="form.name"
                  class="form-control"
                  id="name"
                  maxlength="50"
                  required
                >
              </div>

              <div class="mb-3">
                <label for="description" class="form-label fw-medium">{{ $t('__aiTypeDescription') }}</label>
                <textarea
                  v-model="form.description"
                  class="form-control"
                  id="description"
                  rows="3"
                ></textarea>
              </div>

              <div class="mb-3">
                <label for="sort_order" class="form-label fw-medium">{{ $t('__aiTypeSortOrder') }}</label>
                <input
                  type="number"
                  v-model.number="form.sort_order"
                  class="form-control"
                  id="sort_order"
                >
              </div>

              <div class="mb-3">
                <div class="form-check form-switch">
                  <input
                    type="checkbox"
                    v-model="form.is_active"
                    class="form-check-input"
                    id="isActive"
                  >
                  <label class="form-check-label" for="isActive">
                    {{ $t('__active') }}
                  </label>
                </div>
              </div>
            </div>
            <div class="modal-footer justify-content-between">
              <button
                type="button"
                class="btn btn-outline-secondary rounded-pill"
                data-bs-dismiss="modal"
              >
                {{ $t('__cancel') }}
              </button>
              <button type="submit" class="btn btn-primary rounded-pill">
                {{ $t('__save') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { aiKnowledge } from '../http/api/aiKnowledge'
import { mapMutations } from 'vuex'
import i18n from '@/lang/lang.js'
import { Modal } from 'bootstrap'

export default {
  name: 'AiTypes',
  data() {
    return {
      loading: false,
      types: [],
      fields: [
        { key: 'sort_order', label: this.$t('__aiTypeSortOrder'), thStyle: { width: '80px' }, tdClass: 'align-middle' },
        { key: 'code', label: this.$t('__aiTypeCode'), thStyle: { width: '120px' }, tdClass: 'align-middle' },
        { key: 'name', label: this.$t('__aiTypeName'), tdClass: 'align-middle' },
        { key: 'description', label: this.$t('__aiTypeDescription'), tdClass: 'align-middle' },
        { key: 'is_active', label: this.$t('__status'), thStyle: { width: '80px' }, tdClass: 'align-middle text-center' },
        { key: 'actions', label: this.$t('__actions'), thStyle: { width: '100px' }, tdClass: 'align-middle' }
      ],
      form: {
        id: null,
        code: '',
        name: '',
        description: '',
        sort_order: 0,
        is_active: true
      },
      bootstrap_modal: null
    }
  },
  mounted() {
    this.loadTypes()
  },
  methods: {
    ...mapMutations(['Loading', 'Loaded', 'AlertsInfo', 'Alerted']),

    loadTypes() {
      const vm = this
      vm.loading = true
      vm.Loading()
      const token = localStorage.getItem('humetrics_token')

      aiKnowledge.types.get(token).then((res) => {
        if (res.status <= 201 && res.data.status === 'success') {
          vm.types = res.data.data
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: i18n.t('__aiGetTypeFailed')
          })
          vm.Alerted()
        }
        vm.loading = false
        vm.Loaded()
      }).catch((error) => {
        console.error(i18n.t('__aiGetTypeFailed'), error)
        vm.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: i18n.t('__aiGetTypeFailed')
        })
        vm.Alerted()
        vm.loading = false
        vm.Loaded()
      })
    },

    showModal(id = null) {
      const vm = this
      if (id) {
        const type = vm.types.find(t => t.id === id)
        if (type) {
          vm.form = { ...type }
        }
      } else {
        vm.form = {
          id: null,
          code: '',
          name: '',
          description: '',
          sort_order: 0,
          is_active: true
        }
      }
      vm.bootstrap_modal = new Modal(vm.$refs.typeModal)
      vm.$nextTick(function () {
        vm.bootstrap_modal.show()
      })
    },

    saveType(e) {
      if (e) {
        e.preventDefault()
      }

      const vm = this
      const token = localStorage.getItem('humetrics_token')

      if (!vm.form.code || !vm.form.name) {
        vm.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: i18n.t('__fillRequiredFields')
        })
        vm.Alerted()
        return
      }

      vm.Loading()
      const apiCall = vm.form.id
        ? aiKnowledge.types.update(vm.form.id, vm.form, token)
        : aiKnowledge.types.create(vm.form, token)

      apiCall.then((res) => {
        if (res.status <= 201 && res.data.status === 'success') {
          vm.AlertsInfo({
            state: 'success',
            title: i18n.t('__success'),
            info: vm.form.id ? i18n.t('__editSuccess') : i18n.t('__createSuccess')
          })
          vm.Alerted()
          vm.bootstrap_modal.hide()
          vm.loadTypes()
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: res.data.message || i18n.t('__aiOperationFailed')
          })
          vm.Alerted()
        }
        vm.Loaded()
      }).catch((error) => {
        console.error(i18n.t('__saveFailed'), error)
        if (error.response && error.response.data && error.response.data.errors) {
          const firstError = Object.values(error.response.data.errors)[0][0]
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: firstError
          })
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: error.response?.data?.message || i18n.t('__saveFailed')
          })
        }
        vm.Alerted()
        vm.Loaded()
      })
    },

    updateType(type) {
      const vm = this
      const token = localStorage.getItem('humetrics_token')

      vm.Loading()
      aiKnowledge.types.update(type.id, type, token).then((res) => {
        if (res.status <= 201 && res.data.status === 'success') {
          // 更新成功後重新載入，以顯示正確的排序
          vm.loadTypes()
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: i18n.t('__updateError')
          })
          vm.Alerted()
          vm.loadTypes()
        }
        vm.Loaded()
      }).catch((error) => {
        console.error(i18n.t('__updateError'), error)
        vm.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: i18n.t('__updateError')
        })
        vm.Alerted()
        vm.Loaded()
        vm.loadTypes()
      })
    },

    toggleStatus(type) {
      const vm = this
      const token = localStorage.getItem('humetrics_token')
      type.is_active = !type.is_active

      vm.Loading()
      aiKnowledge.types.update(type.id, type, token).then((res) => {
        if (res.status <= 201 && res.data.status === 'success') {
          // 靜默更新，不顯示訊息
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: i18n.t('__updateStatusFailed')
          })
          vm.Alerted()
          vm.loadTypes()
        }
        vm.Loaded()
      }).catch((error) => {
        console.error(i18n.t('__updateStatusFailed'), error)
        vm.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: i18n.t('__updateStatusFailed')
        })
        vm.Alerted()
        vm.Loaded()
        vm.loadTypes()
      })
    },

    confirmDelete(type) {
      const vm = this
      const token = localStorage.getItem('humetrics_token')

      if (confirm(this.$t('__deleteAiTypeNote', { name: type.name }))) {
        vm.Loading()
        aiKnowledge.types.delete(type.id, token).then((res) => {
          if (res.status <= 201 && res.data.status === 'success') {
            vm.AlertsInfo({
              state: 'success',
              title: i18n.t('__success'),
              info: i18n.t('__deleteSuccess')
            })
            vm.Alerted()
            vm.loadTypes()
          } else {
            vm.AlertsInfo({
              state: 'error',
              title: i18n.t('__error'),
              info: res.data.message || i18n.t('__aiDeleteFailed')
            })
            vm.Alerted()
          }
          vm.Loaded()
        }).catch((error) => {
          console.error(i18n.t('__aiDeleteFailed'), error)
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: error.response?.data?.message || i18n.t('__aiDeleteFailed')
          })
          vm.Alerted()
          vm.Loaded()
        })
      }
    }
  }
}
</script>

<style scoped>
.form-label.required:after {
  content: " *";
  color: #dc3545;
}
</style>
